import type { EpochRecord, Manifest } from "../data/types";
import { epochDescriptor, formatOffsetKms, hbetaOffsetKms } from "../data/types";

import type { Mode } from "../domain/mode";

interface Props {
  object: Manifest["object"];
  epochs: EpochRecord[];
  primaryIndex: number;
  comparisonIndex: number | null;
  onSelectPrimary: (index: number) => void;
  onSelectComparison: (index: number | null) => void;
  mode: Mode;
  cursorMjd: number;
  showInterpolated: boolean;
}

function formatNumber(value: number, digits = 0): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function provenanceDisplayLabel(prov: EpochRecord["provenance"]): string {
  switch (prov) {
    case "observed":
      return "Observed";
    case "user_provided":
      return "User-provided FITS";
    default:
      return "Synthetic (Table 1)";
  }
}

export function EpochInfo(props: Props) {
  const {
    object,
    epochs,
    primaryIndex,
    comparisonIndex,
    onSelectPrimary,
    onSelectComparison,
    mode,
    cursorMjd,
    showInterpolated,
  } = props;

  const primary = epochs[primaryIndex];
  const primaryHbetaOffset = hbetaOffsetKms(primary);
  const comparison = comparisonIndex !== null ? epochs[comparisonIndex] : null;

  const provenanceLabel = provenanceDisplayLabel(primary.provenance);
  const hasNativeMeta =
    primary.provenance !== "synthetic_table1" &&
    (primary.native_lambda_obs_min_A != null ||
      primary.native_lambda_obs_max_A != null ||
      primary.native_resolution_R != null);

  return (
    <div className="info-panel panel">
      <h2>Object</h2>
      <div className="measurement-grid">
        <span className="key">Name</span>
        <span className="value">{object.name}</span>
        <span className="key">Shorthand</span>
        <span className="value">{object.shorthand}</span>
        <span className="key">Redshift z</span>
        <span className="value">{object.z}</span>
      </div>

      <div className="panel-section">
        <h2>Current epoch</h2>
        <div className="measurement-grid">
          <span className="key">Date</span>
          <span className="value">{primary.date}</span>
          <span className="key">MJD</span>
          <span className="value">{primary.mjd}</span>
          <span className="key">Telescope</span>
          <span className="value">{primary.telescope}</span>
          <span className="key">Instrument</span>
          <span className="value">{primary.instrument}</span>
          {primary.state ? (
            <>
              <span className="key">State</span>
              <span className="value">
                <span className={`state-tag ${primary.state.toLowerCase()}`}>{primary.state}</span>
              </span>
            </>
          ) : null}
          {primaryHbetaOffset !== null ? (
            <>
              <span className="key">Hβ peak offset</span>
              <span className="value">{formatOffsetKms(primaryHbetaOffset)}</span>
            </>
          ) : null}
          <span className="key">Provenance</span>
          <span className="value">
            <span className={`provenance-badge ${primary.provenance}`}>
              {provenanceLabel}
            </span>
          </span>
          {primary.source ? (
            <>
              <span className="key">Source</span>
              <span className="value" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                {primary.source}
              </span>
            </>
          ) : null}
        </div>

        {hasNativeMeta ? (
          <div className="measurement-grid" style={{ marginTop: 8 }}>
            {primary.native_lambda_obs_min_A != null && primary.native_lambda_obs_max_A != null ? (
              <>
                <span className="key">Native λ_obs (Å)</span>
                <span className="value">
                  {primary.native_lambda_obs_min_A.toFixed(0)}–
                  {primary.native_lambda_obs_max_A.toFixed(0)}
                </span>
              </>
            ) : null}
            {primary.native_resolution_R != null ? (
              <>
                <span className="key">Resolving power R</span>
                <span className="value">~{primary.native_resolution_R}</span>
              </>
            ) : null}
          </div>
        ) : null}

        {mode === "interpolated" && showInterpolated ? (
          <p style={{ marginTop: 8, color: "var(--warn)", fontSize: 12 }}>
            Cursor at MJD {cursorMjd.toFixed(0)} — display is interpolated between the
            two nearest observed spectra. Treat as a smooth bridge, not measured data.
          </p>
        ) : null}
      </div>

      <div className="panel-section">
        <h2>Measurements (Table 1)</h2>
        <div className="measurement-grid">
          <span className="key">log L₅₁₀₀ (erg/s)</span>
          <span className="value">{primary.measurements.log_l5100.value.toFixed(2)}</span>
          <span className="key">FWHM(Hα) km/s</span>
          <span className="value">{formatNumber(primary.measurements.Halpha.fwhm_kms)}</span>
          <span className="key">F(Hα) ×10⁻¹⁷</span>
          <span className="value">{formatNumber(primary.measurements.Halpha.flux_1e17)}</span>
          <span className="key">FWHM(Hβ) km/s</span>
          <span className="value">{formatNumber(primary.measurements.Hbeta.fwhm_kms)}</span>
          <span className="key">F(Hβ) ×10⁻¹⁷</span>
          <span className="value">{formatNumber(primary.measurements.Hbeta.flux_1e17)}</span>
          <span className="key">F([O III]) ×10⁻¹⁷</span>
          <span className="value">
            {formatNumber(primary.measurements.OIII_5007_flux_1e17.value)}
          </span>
        </div>
      </div>

      <div className="panel-section">
        <h2>Epochs</h2>
        <div className="epoch-table">
          {epochs.map((epoch) => {
            const isCurrent = epoch.index === primaryIndex;
            const isCompare = epoch.index === comparisonIndex;
            return (
              <div
                key={epoch.index}
                className={`row ${isCurrent ? "current" : ""} ${isCompare ? "compare" : ""}`}
                onClick={() => onSelectPrimary(epoch.index)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (epoch.index === comparisonIndex) {
                    onSelectComparison(null);
                  } else if (epoch.index !== primaryIndex) {
                    onSelectComparison(epoch.index);
                  }
                }}
                title="Click to select. Right-click to set/clear comparison."
              >
                <span>
                  {epoch.state ? (
                    <span className={`state-tag ${epoch.state.toLowerCase()}`}>{epoch.state}</span>
                  ) : (
                    <span className="state-tag neutral">{epochDescriptor(epoch)}</span>
                  )}{" "}
                  {epoch.date}
                </span>
                <span style={{ color: "var(--text-dim)" }}>{epoch.telescope.split(" ")[0]}</span>
              </div>
            );
          })}
        </div>
        {comparison ? (
          <p style={{ marginTop: 6, fontSize: 12, color: "var(--warn)" }}>
            Comparing with {comparison.date}
            {epochDescriptor(comparison) ? ` (${epochDescriptor(comparison)})` : ""}. Right-click any row to
            change or clear.
          </p>
        ) : (
          <p style={{ marginTop: 6, fontSize: 12, color: "var(--text-dim)" }}>
            Right-click any row to set as A/B comparison.
          </p>
        )}
      </div>
    </div>
  );
}
